import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, BrowserRouter } from 'react-router'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import theme from '@/shared/theme/theme'
import { useAuthStore, ProtectedRoute, RequireRole, IdleLogoutGuard } from '@/features/auth'
import Layout from '@/shared/components/Layout/Layout'
import NotFoundPage from '@/shared/components/NotFoundPage'
import { ErrorBoundary, Toast } from '@/shared/components/ui'
import { useToastStore } from '@/shared/store/toastStore'
import { extractApiError } from '@/shared/lib/api'

const HomeContent     = lazy(() => import('@/features/home/components/HomeContent'))
const SettingsPage    = lazy(() => import('@/features/settings/components/SettingsPage'))
const FragebogenPage   = lazy(() => import('@/features/fragebogen/components/FragebogenPage'))
const StudieninfosPage = lazy(() => import('@/features/studieninfos/components/StudieninfosPage'))
const KontaktPage         = lazy(() => import('@/features/kontakt/components/KontaktPage'))
const BenutzerprofilPage  = lazy(() => import('@/features/benutzerprofil/components/BenutzerprofilPage'))
const AdminUsersPage           = lazy(() => import('@/features/admin/components/AdminUsersPage'))
const AdminQuestionnairesPage  = lazy(() => import('@/features/admin/components/AdminQuestionnairesPage'))
const AdminAuditPage           = lazy(() => import('@/features/admin/components/AdminAuditPage'))

const PageLoadingFallback = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
    </Box>
)

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            retry: 1,
        },
    },
    queryCache: new QueryCache({
        onError: (error) => {
            useToastStore.getState().show(extractApiError(error), 'error')
        },
    }),
    mutationCache: new MutationCache({
        onError: (error) => {
            useToastStore.getState().show(extractApiError(error), 'error')
        },
    }),
})

const App = () => {
    const { initializeAuth } = useAuthStore()

    useEffect(() => {
        void initializeAuth()
    }, [initializeAuth])

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <ProtectedRoute>
                        <IdleLogoutGuard />
                        <ErrorBoundary>
                            <Suspense fallback={<PageLoadingFallback />}>
                                <Routes>
                                    <Route path="/" element={<Layout />}>
                                        <Route index element={<HomeContent />} />
                                        <Route path="settings" element={<SettingsPage />} />
                                        <Route path="fragenboegen" element={<FragebogenPage />} />
                                        <Route path="studieninfos" element={<StudieninfosPage />} />
                                        <Route path="kontakt" element={<KontaktPage />} />
                                        <Route path="benutzerprofil" element={<BenutzerprofilPage />} />
                                        <Route
                                            path="admin/users"
                                            element={
                                                <RequireRole roles={['administrator', 'study_team']}>
                                                    <AdminUsersPage />
                                                </RequireRole>
                                            }
                                        />
                                        <Route
                                            path="admin/questionnaires"
                                            element={
                                                <RequireRole roles={['administrator']}>
                                                    <AdminQuestionnairesPage />
                                                </RequireRole>
                                            }
                                        />
                                        <Route
                                            path="admin/audit"
                                            element={
                                                <RequireRole roles={['administrator']}>
                                                    <AdminAuditPage />
                                                </RequireRole>
                                            }
                                        />
                                    </Route>
                                    <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                            </Suspense>
                        </ErrorBoundary>
                    </ProtectedRoute>
                    <Toast />
                </ThemeProvider>
            </BrowserRouter>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    )
}

export default App
